import { useSelector as useReduxSelector } from 'react-redux';
import { configureStore } from '@reduxjs/toolkit';
import merge from 'lodash/merge';

import { isDevelopmentEnvironment } from '~/main/environments';
import { HUB_EVENTS } from '~/main/namespace';
import { sentryMiddleware } from '~/main/utils/sentry';
import { isRenderProcess } from '~/renderer/utils';

import { replayActionMain, replayActionRenderer } from './helpers';
import {
  analytics,
  config,
  forwardToMain,
  forwardToRenderer,
  handleLocaleChange,
  logger,
} from './middleware';
import { mainReducer } from './root-reducer';

import type { AnyAction, DeepPartial, Middleware } from '@reduxjs/toolkit';
import type { AppState } from '~/renderer/store/types';
import type { Dictionary } from '~/renderer/utils/types';

export type StoreMiddleware = Middleware<AnyAction, AppState>;

declare module 'react-redux' {
  interface DefaultRootState extends AppState {}
}
const filterMiddlewares = (middlewares: (StoreMiddleware | undefined)[]) =>
  middlewares.filter(Boolean) as StoreMiddleware[];

type MiddlewareEnvironments = 'default' | 'main' | 'renderer';
const middlewareList: Dictionary<MiddlewareEnvironments, StoreMiddleware[]> = {
  default: [logger, handleLocaleChange, config, analytics],
  main: filterMiddlewares([forwardToRenderer, sentryMiddleware, handleLocaleChange]),
  renderer: filterMiddlewares([
    forwardToMain,
    analytics,
    logger,
    handleLocaleChange,
    config,
    sentryMiddleware,
  ]),
};

export const initializeStore = (
  preloadedState?: DeepPartial<AppState>,
  isMainProcess?: boolean,
) => {
  let middleware: Middleware[] = middlewareList.default;

  if (isRenderProcess) middleware = middlewareList.renderer;
  if (isMainProcess) middleware = middlewareList.main;

  // Get the initial state from the reducer
  const defaultState = mainReducer(undefined, { type: '' });
  // Merge it with the preloaded state
  const combinedState = merge({}, defaultState, preloadedState);

  const store = configureStore({
    reducer: mainReducer,
    middleware,
    preloadedState: combinedState,
    devTools: typeof window !== 'undefined',
  });

  if (isMainProcess) replayActionMain(store);
  if (isRenderProcess) replayActionRenderer(store);

  return store;
};

const store = initializeStore();

if (isDevelopmentEnvironment()) {
  module.hot?.accept('./root-reducer', () => {
    store.replaceReducer(mainReducer);
  });
}

if (isRenderProcess) {
  // Hydrate only the current opened window
  window.ipcRenderer?.send(HUB_EVENTS.HYDRATE);
}

export const useSelector = <T>(selector: (state: AppState) => T) =>
  useReduxSelector<AppState, T>(selector);

export default store;
